// Resolves the active default header / footer for a (org, client) scope, the
// same way the API does at compile time — but on the client so live previews
// can substitute `$brand.defaultHeader` / `$brand.defaultFooter` placeholder
// section-refs without a server round trip.
//
// The list endpoint already returns rows in scope-priority order
// (client → org → system, per EmailDocumentSectionsService.list's scopePriority
// CASE expression), so the first isDefault row per category is the correct
// fallback. No extra sorting needed.

import { useMemo } from 'react';
import { useEmailDocumentSectionsControllerList } from '@/apis/backend/generated/email-document-sections/email-document-sections';
import type { EmailDocumentSectionResponseDto } from '@/apis/backend/generated/models/emailDocumentSectionResponseDto';
import type {
  EmailDocumentSectionRootNode,
  SlotDefault,
  SlotSchema,
} from '@nexmail/shared-util/email-builder';

export interface ResolvedDefaultSection {
  node: EmailDocumentSectionRootNode;
  slots: Record<string, SlotSchema>;
  slotDefaults: Record<string, SlotDefault>;
}

export interface UseDefaultSectionsResult {
  defaultHeader: ResolvedDefaultSection | undefined;
  defaultFooter: ResolvedDefaultSection | undefined;
  isLoading: boolean;
}

function pickFirstDefault(
  rows: EmailDocumentSectionResponseDto[] | undefined,
): ResolvedDefaultSection | undefined {
  if (!rows) return undefined;
  // Skip rows whose node payload is missing — html/mjml sourceFormat rows
  // store their content in rawHtml/rawMjml and leave node null, which would
  // crash the preview-side section resolver (it can only clone EmailNode trees).
  const row = rows.find(
    (r) => r.isDefault && r.isActive && r.node && typeof r.node === 'object',
  );
  if (!row) return undefined;
  return {
    node: row.node as unknown as EmailDocumentSectionRootNode,
    slots: row.slots as unknown as Record<string, SlotSchema>,
    slotDefaults: row.slotDefaults as unknown as Record<string, SlotDefault>,
  };
}

export function useDefaultSectionsForScope(
  orgId: string | undefined,
  clientId: string | undefined,
): UseDefaultSectionsResult {
  const enabled = !!(orgId && clientId);

  const headerQuery = useEmailDocumentSectionsControllerList(
    orgId ?? '',
    clientId ?? '',
    { category: 'header', isActive: 'true' },
    { query: { enabled, staleTime: 60_000 } },
  );

  const footerQuery = useEmailDocumentSectionsControllerList(
    orgId ?? '',
    clientId ?? '',
    { category: 'footer', isActive: 'true' },
    { query: { enabled, staleTime: 60_000 } },
  );

  return useMemo(
    () => ({
      defaultHeader: pickFirstDefault(headerQuery.data?.sections),
      defaultFooter: pickFirstDefault(footerQuery.data?.sections),
      isLoading: headerQuery.isLoading || footerQuery.isLoading,
    }),
    [headerQuery.data, footerQuery.data, headerQuery.isLoading, footerQuery.isLoading],
  );
}
